import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProductCard from "@/components/ProductCard";
import { fetchMacbooks } from "@/lib/api";
import { ArrowRight, Shield, CheckCircle, Award, Laptop, Clock, Star } from "lucide-react";

const reviews = [
  {
    name: "Sarah M.",
    location: "Austin, TX",
    rating: 5,
    text: "I was nervous buying a used MacBook online, but this one arrived in amazing condition. Runs like brand new and saved me over $800 compared to Apple's prices.",
    product: "MacBook Air",
  },
  {
    name: "James T.",
    location: "Denver, CO",
    rating: 5,
    text: "Got the extended warranty option and couldn't be happier. The MacBook Pro handles all my video editing without a hitch. Fast shipping too.",
    product: "MacBook Pro",
  },
  {
    name: "Maria L.",
    location: "Chicago, IL",
    rating: 5,
    text: "Bought one for my daughter starting college. She loves it. Great value and the condition was exactly as described — honestly looked brand new.",
    product: "MacBook Air",
  },
  {
    name: "David K.",
    location: "Portland, OR",
    rating: 5,
    text: "Second purchase from PennyMacs. Both times the MacBook exceeded expectations. These guys clearly care about quality. Won't buy anywhere else.",
    product: "MacBook Pro",
  },
  {
    name: "Ashley R.",
    location: "Miami, FL",
    rating: 5,
    text: "The whole process was so simple — picked my model, chose the warranty tier, and it showed up ready to go. No hassle at all.",
    product: "MacBook Air",
  },
  {
    name: "Kevin W.",
    location: "Seattle, WA",
    rating: 4,
    text: "Battery health was great, no scratches, everything works perfectly. Only wish they had more Pro models in stock when I was shopping. Definitely recommend.",
    product: "MacBook Pro",
  },
];

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < rating ? "fill-[#b5954a] text-[#b5954a]" : "text-gray-200"}`}
        />
      ))}
    </div>
  );
}

export default function HomePage({ settings }) {
  const [macbooks, setMacbooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMacbooks()
      .then((res) => setMacbooks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const available = macbooks.filter((m) => m.status === "available");
  const featured = available.slice(0, 4);
  const recentlySold = macbooks.filter((m) => m.status === "sold").slice(0, 6);
  const soldCount = recentlySold.length;

  // Deal of the Day
  const dealEnabled = settings?.deal_enabled && settings?.deal_macbook_id;
  const dealMacbook = dealEnabled ? macbooks.find((m) => m.id === settings.deal_macbook_id && m.status === "available") : null;
  const dealPrice = settings?.deal_price || 0;
  const dealLabel = settings?.deal_label || "Deal of the Day";
  const dealSavings = dealMacbook ? Math.round(dealMacbook.price - dealPrice) : 0;

  const categoryLabels = { air: "MacBook Air", pro: "MacBook Pro" };
  const conditionLabels = { excellent: "Excellent", very_good: "Very Good", good: "Good" };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="hero-gradient pt-20 sm:pt-28 pb-12 sm:pb-16" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-8 px-5 py-2 rounded-full bg-[#0a0a0a]/5 border border-[rgba(181,149,74,0.15)]">
            <div className="w-1.5 h-1.5 rounded-full bg-[#b5954a]" />
            <span className="text-xs font-semibold tracking-widest uppercase text-[#6b6b6b]">
              Certified Pre-Owned
            </span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#0a0a0a] leading-[1.05] max-w-3xl mx-auto">
            MacBooks made
            <br />
            <span className="text-[#b5954a]">simple & affordable</span>
          </h1>
          <p className="mt-7 text-base sm:text-lg text-[#6b6b6b] max-w-lg mx-auto leading-relaxed font-light">
            Choose your model. Pick your tier. That's it.
            Every MacBook is professionally tested and ready to use.
          </p>
          <div className="mt-10 flex items-center justify-center">
            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="hero-warranty-btn"
              className="rounded-full px-8 h-12 text-[13px] font-semibold border-[rgba(181,149,74,0.25)] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-all"
            >
              <Link to="/warranty">Learn About Our Warranty</Link>
            </Button>
          </div>
          {soldCount > 0 && (
            <p className="mt-8 text-sm text-[#9a9a9a] font-light">
              {soldCount} MacBook{soldCount !== 1 ? "s" : ""} already found new homes
            </p>
          )}
        </div>
      </section>

      {/* Deal of the Day */}
      {!loading && dealMacbook && dealPrice > 0 && (
        <section className="py-16 sm:py-20 bg-[#0a0a0a]" data-testid="deal-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Image side */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl bg-[#1a1a1a] overflow-hidden">
                  {dealMacbook.photos?.length > 0 ? (
                    <img
                      src={dealMacbook.photos[0]}
                      alt={categoryLabels[dealMacbook.category]}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Laptop className="w-20 h-20 text-[#b5954a]/30" strokeWidth={1} />
                    </div>
                  )}
                </div>
                {/* Savings badge */}
                {dealSavings > 0 && (
                  <div className="absolute -top-3 -right-3 sm:top-4 sm:right-4 bg-[#b5954a] text-white px-4 py-2 rounded-full shadow-lg">
                    <span className="text-sm font-bold">Save ${dealSavings}</span>
                  </div>
                )}
              </div>

              {/* Details side */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-[#b5954a]/30 bg-[#b5954a]/10">
                  <div className="w-2 h-2 rounded-full bg-[#b5954a] animate-pulse" />
                  <span className="text-xs font-bold tracking-widest uppercase text-[#b5954a]">
                    {dealLabel}
                  </span>
                </div>

                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1] mb-4">
                  {categoryLabels[dealMacbook.category] || dealMacbook.category}
                </h2>

                <p className="text-sm text-[#9a9a9a] mb-2 font-light">
                  {conditionLabels[dealMacbook.condition] || dealMacbook.condition} Condition
                </p>

                {dealMacbook.short_description && (
                  <p className="text-sm text-[#6b6b6b] mb-8 font-light max-w-md mx-auto lg:mx-0">
                    {dealMacbook.short_description}
                  </p>
                )}

                {/* Pricing */}
                <div className="flex items-baseline gap-4 justify-center lg:justify-start mb-8">
                  <span className="text-4xl sm:text-5xl font-bold text-white">
                    ${dealPrice.toLocaleString()}
                  </span>
                  <span className="text-xl text-[#6b6b6b] line-through font-light">
                    ${dealMacbook.price?.toLocaleString()}
                  </span>
                </div>

                <Button
                  asChild
                  size="lg"
                  className="rounded-full px-10 h-13 text-[13px] font-semibold bg-[#b5954a] hover:bg-[#d4b978] text-white transition-colors"
                  data-testid="deal-cta-btn"
                >
                  <Link to={`/product/${dealMacbook.id}`}>Grab This Deal</Link>
                </Button>

                <p className="mt-5 text-xs text-[#6b6b6b] font-light">
                  Limited availability — once it's gone, it's gone.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured MacBooks */}
      {!loading && featured.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="featured-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="gold-line mb-4" />
                <h2 className="font-serif text-2xl sm:text-3xl text-[#0a0a0a]">
                  Available Now
                </h2>
                <p className="mt-2 text-sm text-[#6b6b6b] font-light">
                  Each MacBook is one-of-a-kind. When it's gone, it's gone.
                </p>
              </div>
              {available.length > 4 && (
                <Link
                  to="/shop"
                  data-testid="view-all-link"
                  className="hidden sm:flex items-center gap-1.5 text-[13px] font-medium text-[#b5954a] hover:text-[#0a0a0a] transition-colors"
                >
                  View all {available.length} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
              {featured.map((mb) => (
                <ProductCard key={mb.id} macbook={mb} />
              ))}
            </div>
            <div className="sm:hidden mt-10 text-center">
              {available.length > 4 && (
                <Button asChild variant="outline" className="rounded-full border-[rgba(181,149,74,0.25)]" data-testid="view-all-btn-mobile">
                  <Link to="/shop">View all MacBooks</Link>
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!loading && macbooks.length === 0 && (
        <section className="py-24 sm:py-32 bg-white" data-testid="empty-state-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <Laptop className="w-14 h-14 text-[#d4b978] mx-auto mb-6" strokeWidth={1} />
            <h2 className="font-serif text-xl sm:text-2xl text-[#0a0a0a]">
              Fresh inventory coming soon
            </h2>
            <p className="mt-3 text-sm text-[#6b6b6b] max-w-md mx-auto font-light">
              We're preparing our next batch of certified pre-owned MacBooks. Check back soon.
            </p>
          </div>
        </section>
      )}

      {/* Customer Reviews */}
      <section className="py-20 sm:py-28 bg-[#f6f5f1]" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1.5 mb-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-4.5 h-4.5 fill-[#b5954a] text-[#b5954a]" />
              ))}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0a0a0a]">
              What Our Customers Say
            </h2>
            <p className="mt-3 text-sm text-[#6b6b6b] font-light">
              Real experiences from real MacBook buyers
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {reviews.map((review, i) => (
              <div
                key={i}
                className="review-card bg-white rounded-2xl p-7 flex flex-col"
                data-testid={`review-card-${i}`}
              >
                <StarRating rating={review.rating} />
                <p className="mt-5 text-sm text-[#0a0a0a] leading-relaxed flex-1 font-light">
                  "{review.text}"
                </p>
                <div className="mt-6 pt-5 border-t border-[rgba(181,149,74,0.1)] flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0a0a0a]">{review.name}</p>
                    <p className="text-xs text-[#9a9a9a]">{review.location}</p>
                  </div>
                  <span className="text-[11px] font-medium text-[#b5954a] bg-[#f6f5f1] px-3 py-1 rounded-full border border-[rgba(181,149,74,0.15)]">
                    {review.product}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 sm:py-28" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-5" />
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0a0a0a]">
              How It Works
            </h2>
            <p className="mt-3 text-sm text-[#6b6b6b] font-light">
              Buying a MacBook shouldn't be complicated
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Choose your model",
                desc: "MacBook Air for everyday tasks, or MacBook Pro for heavier work.",
              },
              {
                step: "02",
                title: "Pick your tier",
                desc: "Basic includes MacBook + charger. Extended Warranty adds 90-day protection.",
              },
              {
                step: "03",
                title: "Buy with confidence",
                desc: "Every MacBook is tested, verified, and ready to use out of the box.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="trust-card p-8 rounded-2xl bg-white border border-[rgba(181,149,74,0.08)]"
                data-testid={`how-step-${i + 1}`}
              >
                <span className="text-[11px] font-bold text-[#b5954a] tracking-[0.2em]">
                  STEP {item.step}
                </span>
                <h3 className="mt-4 font-serif text-lg text-[#0a0a0a]">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-[#6b6b6b] leading-relaxed font-light">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recently Sold */}
      {recentlySold.length > 0 && (
        <section className="py-16 sm:py-24 bg-white border-t border-[rgba(181,149,74,0.08)]" data-testid="recently-sold-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#0a0a0a]/5 text-[#6b6b6b] px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-wider mb-4">
                <Clock className="w-3 h-3 text-[#b5954a]" />
                RECENTLY SOLD
              </div>
              <h2 className="font-serif text-xl sm:text-2xl text-[#0a0a0a]">
                These MacBooks Found New Homes
              </h2>
              <p className="mt-2 text-sm text-[#6b6b6b] font-light">
                Don't wait too long — our MacBooks sell fast.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              {recentlySold.map((mb) => {
                const img = mb.photos?.[0];
                const imgUrl = img || null;
                const categoryLabels = { air: "MacBook Air", pro: "MacBook Pro" };
                const tierLabels = { basic: "Basic", warranty: "Extended Warranty" };
                return (
                  <Link
                    key={mb.id}
                    to={`/product/${mb.id}`}
                    className="group text-center"
                    data-testid={`recently-sold-${mb.id}`}
                  >
                    <div className="relative aspect-square rounded-xl bg-[#f6f5f1] overflow-hidden mb-3">
                      {imgUrl ? (
                        <img src={imgUrl} alt="" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Laptop className="w-8 h-8 text-[#d4b978]" strokeWidth={1} />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="px-3 py-1 bg-white/90 text-[#0a0a0a] text-[10px] font-bold rounded-full tracking-widest shadow-sm">
                          SOLD
                        </span>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-[#0a0a0a]">
                      {categoryLabels[mb.category] || mb.category}
                    </p>
                    <p className="text-[10px] text-[#9a9a9a]">
                      {tierLabels[mb.tier] || mb.tier}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Trust Signals */}
      <section className="py-20 sm:py-28 bg-[#f6f5f1]" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-5" />
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0a0a0a]">
              Why PennyMacs
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {[
              { icon: CheckCircle, title: "Professionally Tested", desc: "Every MacBook undergoes thorough testing before listing." },
              { icon: Shield, title: "Warranty Available", desc: "Extended warranty option for extra peace of mind." },
              { icon: Award, title: "Quality Guaranteed", desc: "Only Excellent, Very Good, and Good condition MacBooks." },
              { icon: Laptop, title: "Ready to Use", desc: "Clean install, charger included. Open the box and go." },
            ].map((item, i) => (
              <div
                key={i}
                className="trust-card p-7 rounded-2xl bg-white text-center border border-[rgba(181,149,74,0.08)]"
                data-testid={`trust-card-${i}`}
              >
                <item.icon className="w-7 h-7 text-[#b5954a] mx-auto mb-5" strokeWidth={1.5} />
                <h3 className="text-sm font-semibold text-[#0a0a0a]">{item.title}</h3>
                <p className="mt-2 text-xs text-[#6b6b6b] leading-relaxed font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="gold-line mx-auto mb-6" />
          <h2 className="font-serif text-2xl sm:text-3xl text-[#0a0a0a]">
            Ready to Find Your MacBook?
          </h2>
          <p className="mt-4 text-sm text-[#6b6b6b] font-light">
            No specs to decode. No confusion. Just simple, trusted MacBooks.
          </p>
          <Button
            asChild
            size="lg"
            className="mt-10 rounded-full px-10 h-13 text-[13px] font-semibold bg-[#0a0a0a] hover:bg-[#b5954a] transition-colors"
            data-testid="cta-shop-btn"
          >
            <Link to="/shop">Browse Available MacBooks</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
